import React, {useEffect, useRef, useState} from 'react';
import { useDirtyTracker } from "../../../../../util/useDirtyTracker";
import { useTranslation } from 'react-i18next';
import './BusinessCalendarComponent.css';
import './BusinessNotificationComponent.css';
import { BusinessSettings } from "../../../../../model/business/BusinessSettings";
import {
    findBusinessSettingByBusinessId, saveBusinessNotificationSettings,
} from "../../../../../service/business/business-service";
import { useAuth0 } from '../../../../../auth/useAuth0';
import { useBusinessContext } from "../../../../../context/BusinessContext";
import { useAlert } from "../../../../../context/alert/AlertContext";
import ActionsButtonsComponent from "../../../../commons/action-buttons/ActionsButtonsComponent";
import {mapSourceToTarget} from "../../../../../util/util";
import {BusinessNotificationSettingsInput} from "../../../../../service/business/BusinessNotificationSettingsInput";
import NotificationEventCard from "./NotificationEventCard";
import { NOTIFICATION_STAGES, SettingsField } from "./notificationEvents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

const BusinessNotificationComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently, logout } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    // Estados locais
    const [settingsData, setSettingsData] = useState<BusinessSettings | null>(null);
    // QA §5.10: dirty DERIVADO de baseline (não boolean por onChange).
    const { isModified, setBaseline } = useDirtyTracker(settingsData);


    // Which lifecycle-stage sections are expanded (accordion — multiple may be
    // open at once; ALL start closed so the screen opens as a clean overview).
    const [openStages, setOpenStages] = useState<Record<string, boolean>>({});
    const toggleStage = (key: string) =>
        setOpenStages((prev) => ({ ...prev, [key]: !prev[key] }));

    // One ref per text field, keyed by field name, so variable insertion can
    // target the right textarea's cursor in the data-driven card layout.
    const textRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
    const registerTextRef = (field: SettingsField, el: HTMLTextAreaElement | null) => {
        textRefs.current[field as string] = el;
    };

    /**
     * Inserts `token` at the cursor position of the textarea bound to `field`,
     * then updates controlled state.  Falls back to appending when the textarea
     * isn't mounted or has no stored selection.
     */
    const insertAtCursor = (field: SettingsField, token: string) => {
        const el = textRefs.current[field as string];
        const current: string = ((settingsData?.[field as keyof BusinessSettings] as unknown) as string | undefined) ?? '';
        let next: string;

        if (el) {
            const start = el.selectionStart ?? current.length;
            const end = el.selectionEnd ?? current.length;
            next = current.slice(0, start) + token + current.slice(end);

            // Restore focus and move caret to just after the inserted token
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start + token.length, start + token.length);
            });
        } else {
            next = current + token;
        }

        handleInputChange(field, next);
    };

    // Derived business id for preview (coerce ID string → number)
    const previewBusinessId: number | null =
        selectedBusiness?.id != null ? Number(selectedBusiness.id) : null;

    // Business name for the {business} sample variable in the SMS preview.
    const previewBusinessName: string | null = selectedBusiness?.name ?? null;

    // Fetch Business Settings
    useEffect(() => {
        if (settingsData) {
            // Se os dados já estão carregados, não faz nada
            return;
        }
        const fetchBusinessSetting = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (!selectedBusiness || !selectedBusiness.id) {
                    setAlert('danger', t('settings.business.alerts.settingsNotLoaded'));
                    return;
                }
                const fetchedBusinessSetting = await findBusinessSettingByBusinessId(selectedBusiness.id, token);
                if (fetchedBusinessSetting) {
                    if (selectedBusiness.id !== fetchedBusinessSetting.businessId) {
                        await logout();
                    }
                    setBaseline(fetchedBusinessSetting);
                    setSettingsData(fetchedBusinessSetting);
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setAlert('danger', t('settings.business.alerts.settingsFetchError', { error: errorMessage }));
            }
        };

        fetchBusinessSetting();
    }, [getAccessTokenSilently, selectedBusiness, logout, setAlert, settingsData]);

    // Save Business Settings
    const handleSave = async () => {
        if (!settingsData) return;

        try {
            const token = await getAccessTokenSilently();
            const input: BusinessNotificationSettingsInput = mapSourceToTarget(settingsData, BusinessNotificationSettingsInput);
            const result = await saveBusinessNotificationSettings(input, token);

            if (result.success) {
                setAlert('success', t('settings.business.alerts.notificationSaved'));
                setBaseline(settingsData); // o salvo vira o novo baseline limpo
            } else {
                setAlert('danger', t('settings.business.alerts.notificationSaveError', { error: result.error }));
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setAlert('danger', t('settings.business.alerts.notificationSaveErrorGeneric', { error: errorMessage }));
        }
    };

    // Cancel Modifications
    const handleCancel = () => {
        // Recarrega o original → dirty volta a false (baseline resetado).
        const reloadBusinessSettings = async () => {
            const token = await getAccessTokenSilently();
            const fetchedBusinessSetting = await findBusinessSettingByBusinessId(selectedBusiness!.id, token);
            setBaseline(fetchedBusinessSetting);
            setSettingsData(fetchedBusinessSetting);
        };
        reloadBusinessSettings();
    };

    type BusinessSettingsField = keyof BusinessNotificationSettingsInput | `${keyof BusinessNotificationSettingsInput & string}.${string}`;

    const handleInputChange = (field: BusinessSettingsField, value: any) => {
        setSettingsData((prevData) => {
            if (!prevData) {
                // Retorna o estado original se `prevData` for `null`
                return prevData;
            }

            if (field.includes('.')) {
                const [parentKey, childKey] = field.split('.') as [keyof BusinessNotificationSettingsInput, string];

                // Verifique se `prevData[parentKey]` é um objeto válido
                const parentValue = typeof prevData[parentKey] === 'object' && prevData[parentKey] !== null
                    ? prevData[parentKey]
                    : {};

                return {
                    ...prevData,
                    [parentKey]: {
                        ...parentValue,
                        [childKey]: value,
                    },
                };
            } else {
                return {
                    ...prevData,
                    [field]: value,
                };
            }
        });
    };


    // if (!settingsData) {
    //     return <div>Loading settings...</div>;
    // }

    return (
        <>
            <ActionsButtonsComponent
                isModified={isModified}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="side-content-vertical">
                <div className="business-section-container separated-container">
                    <h3 className="section-header">{t('settings.notification.sectionTitle')}</h3>
                    <p className="notif-intro">
                        {t('settings.notification.intro')}
                    </p>

                    {/* Team notifications — e-mail to the assigned professional (+ owner)
                        when a CLIENT acts: books online, cancels, answers a reschedule.
                        Unset (null) = on, so existing businesses get them by default. */}
                    <div className="notif-team" data-cy="biz-notif-team">
                        <div className="notif-team__head">
                            <span className="notif-team__title">{t('settings.notification.team.title')}</span>
                            <span className="notif-team__blurb">{t('settings.notification.team.blurb')}</span>
                        </div>
                        <div className="notif-team__grid">
                            {([
                                ['notifyTeamNewBooking', 'newBooking'],
                                ['notifyTeamCancellation', 'cancellation'],
                                ['notifyTeamReschedule', 'reschedule'],
                            ] as [SettingsField, string][]).map(([field, key]) => (
                                <label key={field} className="gs-inline-check notif-team__item">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        data-cy={`biz-notif-team-${key}`}
                                        checked={settingsData?.[field as keyof BusinessSettings] !== false}
                                        onChange={(e) => handleInputChange(field, e.target.checked)}
                                    />
                                    <span className="notif-team__text">
                                        <span className="notif-team__label">{t(`settings.notification.team.${key}`)}</span>
                                        <span className="notif-team__hint">{t(`settings.notification.team.${key}Hint`)}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                        <label className="gs-inline-check notif-team__owner">
                            <input
                                type="checkbox"
                                className="gs-inline-check__box"
                                data-cy="biz-notif-team-include-owner"
                                checked={settingsData?.notifyTeamIncludeOwner !== false}
                                onChange={(e) => handleInputChange('notifyTeamIncludeOwner', e.target.checked)}
                            />
                            <span className="notif-team__text">
                                <span className="notif-team__label">{t('settings.notification.team.includeOwner')}</span>
                                <span className="notif-team__hint">{t('settings.notification.team.includeOwnerHint')}</span>
                            </span>
                        </label>
                    </div>

                    {/* Collapsible sections, one per lifecycle stage */}
                    <div className="notif-accordion">
                        {NOTIFICATION_STAGES.map((stage) => {
                            const open = !!openStages[stage.key];
                            return (
                                <section
                                    key={stage.key}
                                    className={`notif-section${open ? ' notif-section--open' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className="notif-section__header"
                                        aria-expanded={open}
                                        data-cy={`biz-notif-section-${stage.key}`}
                                        onClick={() => toggleStage(stage.key)}
                                    >
                                        <span className="notif-section__headtext">
                                            <span className="notif-section__titlerow">
                                                <span className="notif-section__title">{t(stage.labelKey)}</span>
                                                <span className="notif-section__count">{stage.events.length}</span>
                                            </span>
                                            <span className="notif-section__blurb">{t(stage.blurbKey)}</span>
                                        </span>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className={`notif-section__chevron${open ? ' notif-section__chevron--open' : ''}`}
                                        />
                                    </button>

                                    {open && (
                                        <div className="notif-event-grid">
                                            {stage.events.map((event) => (
                                                <NotificationEventCard
                                                    key={event.key}
                                                    event={event}
                                                    settingsData={settingsData}
                                                    onToggle={(field, checked) => handleInputChange(field, checked)}
                                                    onText={(field, value) => handleInputChange(field, value)}
                                                    onInsertVar={insertAtCursor}
                                                    registerTextRef={registerTextRef}
                                                    previewBusinessId={previewBusinessId}
                                                    previewBusinessName={previewBusinessName}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BusinessNotificationComponent;
